import React, { useState, useEffect } from 'react';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import HomePage from './components/pages/HomePage';
import ProductPage from './components/pages/ProductPage';
import PricingPage from './components/pages/PricingPage';
import AboutPage from './components/pages/AboutPage';
import ContactPage from './components/pages/ContactPage';
import PrivacyPolicyPage from './components/pages/PrivacyPolicyPage';
import CopyrightPage from './components/pages/CopyrightPage';
import DeleteAccountPage from './components/pages/DeleteAccountPage';
import FeaturePage from './components/pages/FeaturePage';
import DownloadPage from './components/pages/DownloadPage';
import { getProductFeatures } from './data/productFeatures';
import { useLang } from './i18n/LanguageContext';
import './App.css';

const PATH_ROUTES = ['download', 'delete-account'];

function App({ initialPage = 'home' }) {
  const [currentPage, setCurrentPage] = useState(initialPage || 'home');
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { lang } = useLang();
  const productFeatures = getProductFeatures(lang);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  useEffect(() => {
    const handlePopState = () => {
      const pathname = window.location.pathname.replace(/^\//, '').replace(/\/$/, '');
      const params = new URLSearchParams(window.location.search);
      const page = PATH_ROUTES.includes(pathname) ? pathname : (params.get('page') || 'home');
      setCurrentPage(page);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const updateUrlPageParam = (page) => {
    if (page === 'home') {
      window.history.pushState({}, '', '/');
    } else if (PATH_ROUTES.includes(page)) {
      window.history.pushState({}, '', `/${page}`);
    } else {
      window.history.pushState({}, '', `/?page=${page}`);
    }
  };

  const handleNavClick = (page, url) => {
    if (url) {
      window.open(url, '_blank');
      return;
    }
    // Handle legacy page names
    const pageMap = {
      'sapot-note': 'product',
      'faq': 'pricing',
      'careers': 'about',
      'press': 'about',
      'business': 'product',
      'sales': 'product',
      'managers': 'product',
    };
    const resolvedPage = pageMap[page] || page;
    setCurrentPage(resolvedPage);
    setMobileMenuOpen(false);
    updateUrlPageParam(resolvedPage);
  };

  const renderPage = () => {
    if (currentPage.startsWith('feature-')) {
      const featureId = currentPage.replace('feature-', '');
      const feature = productFeatures.find(f => f.id === featureId);
      if (feature) return <FeaturePage feature={feature} onNavigate={handleNavClick} />;
    }

    switch (currentPage) {
      case 'product': return <ProductPage onNavigate={handleNavClick} />;
      case 'pricing': return <PricingPage />;
      case 'about': return <AboutPage onNavigate={handleNavClick} />;
      case 'contact': return <ContactPage />;
      case 'privacyPolicy': return <PrivacyPolicyPage />;
      case 'copyright': return <CopyrightPage />;
      case 'delete-account': return <DeleteAccountPage />;
      case 'download': return <DownloadPage onNavigate={handleNavClick} />;
      default: return <HomePage onNavigate={handleNavClick} />;
    }
  };

  return (
    <div className="app-container app-en">
      <Navbar
        currentPage={currentPage}
        isScrolled={isScrolled}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        onNavigate={handleNavClick}
      />
      <main>
        {renderPage()}
      </main>
      {currentPage !== 'download' && <Footer onNavigate={handleNavClick} />}
    </div>
  );
}

export default App;
